/**
 * Aura 전용 문서 자동 채움.
 *
 * ── 원본 문서를 읽고 파악한 "업무 단위" ─────────────────────────────
 *   Share Application Form   : 개인주주 1인당 1부. "I hereby apply…" 1인칭 +
 *                              Full Name / **Passport No.** / Address → 개인 주주용.
 *   DDF (Disclosure of Interest) : 이사 1인당 1부. Companies Act s.156/s.165 에 따라
 *                              이사 각자가 본인 명의로 제출하는 개인 선언서
 *                              (원본 하단 [[director_name]] 1개, 반복 블록 없음).
 *   First BOD Resolution     : 회사 1부. 이사회 결의서.
 *                              ※ [[Number_Of_Shares]] 는 "allotment of Company's N ordinary
 *                                shares" → **회사 총 발행주식수**(특정 주주 것이 아님),
 *                                [[Price_Per_Share]] 는 주당 가격.
 *   Incorporation Documents  : 회사 1부. 서비스계약·위임장·면책서·결의서·Form A1·
 *                              노미니 이사 계약 번들.
 *   Foreign Director/SH      : 회사 1부, 표 안에서 외국 국적자만 행으로 반복.
 *
 * 원칙
 *  - CAF 에서 온 값(이름·ID·주소 등)은 **사용자가 직접 고치지 않은 동안 CAF 를 따라간다**
 *    (followSource). 예전엔 '비어 있을 때만' 채워서, CAF 에서 이사 이름을 바꿔도
 *    Signing Director·DDF·Share Application 등에 옛 이름이 남았다.
 *  - 고정 기본값(체크박스 기본 등)은 비어 있을 때만 채운다(fillIf).
 *  - 원천이 없는 항목(수수료·증인·법원·계약일·작성일)은 비워 둔다.
 *  - 서명란은 기존 SG 관례대로 "이름 텍스트".
 */

function str(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

type SetValue = (key: string, val: string) => void;

function fillIf(
  values: Record<string, string>,
  setValue: SetValue,
  target: string,
  value: string,
) {
  if (!value) return;
  if (str(values[target])) return;
  setValue(target, value);
}

/** values 객체별 "마지막으로 자동으로 넣은 값" — 사용자가 고친 칸인지 판별용 */
const lastAuto = new WeakMap<object, Map<string, string>>();

/**
 * 원천(CAF) 값을 따라가되, 사용자가 직접 고친 칸은 건드리지 않는다.
 *   - 칸이 비었거나, 마지막 자동값과 같거나, (Edit 재진입 직후라 기록이 없고) 지금 원천값과
 *     같으면 → 사용자가 손대지 않은 칸 → 원천값으로 갱신(원천이 비면 칸도 비움).
 *   - 그 밖(마지막 자동값과 다른 값) → 사용자 입력 → 보존.
 */
function followSource(
  values: Record<string, string>,
  setValue: SetValue,
  target: string,
  value: string,
) {
  let mem = lastAuto.get(values);
  if (!mem) {
    mem = new Map();
    lastAuto.set(values, mem);
  }
  const cur = str(values[target]);
  const prev = mem.get(target);
  const untouched = !cur || cur === prev || cur === value;
  if (!untouched) return;
  if (cur !== value) setValue(target, value);
  mem.set(target, value);
}

/**
 * T-Link 자동채움(패널 autofill)이 직접 써 넣은 값을 "자동값"으로 기록한다.
 *   예) 매퍼가 최상위 secretary_name 을 바로 채우면, 동기화 입장에선 "기록에 없는 값 = 사용자 입력"
 *   으로 보여 CAF 비서 이름을 바꿔도 First Board 비서가 따라가지 않았다(X-5).
 *   여기 기록해 두면 followSource 가 원천(CAF) 변경을 따라간다.
 */
export function markAutoValues(values: Record<string, string>, keys: Iterable<string>) {
  let mem = lastAuto.get(values);
  if (!mem) {
    mem = new Map();
    lastAuto.set(values, mem);
  }
  for (const k of keys) mem.set(k, str(values[k]));
}

/** 이름 키에 값이 있는 행 id (CAF 에서 지운 행은 값이 비워지므로 빠진다) */
function namedIds(values: Record<string, string>, prefix: string, suffix: string): number[] {
  const out: number[] = [];
  const re = new RegExp(`^${prefix}(\\d+)_${suffix}$`);
  for (const k of Object.keys(values)) {
    const m = re.exec(k);
    if (m && str(values[k])) out.push(Number(m[1]));
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

/** `prefix{N}_suffix` 중 값이 있는 최대 N */
function maxIndexed(values: Record<string, string>, prefix: string, suffix: string): number {
  let max = 0;
  const re = new RegExp(`^${prefix}(\\d+)_${suffix}$`);
  for (const k of Object.keys(values)) {
    const m = re.exec(k);
    if (m && str(values[k])) max = Math.max(max, Number(m[1]));
  }
  return max;
}

/** 싱가포르 국적이면 local, 그 외(값이 있을 때)만 foreign */
function isForeignNationality(nationality: string): boolean {
  const n = str(nationality).toLowerCase();
  if (!n) return false; // 미입력은 판단 불가 → 제외
  return !/singapore|singaporean|\bsg\b/.test(n);
}

/** 숫자 문자열 합계(콤마 허용). 하나도 못 읽으면 '' */
function sumNumeric(list: string[]): string {
  let total = 0;
  let any = false;
  for (const s of list) {
    const n = Number(String(s).replace(/[, ]/g, ''));
    if (Number.isFinite(n) && String(s).trim()) {
      total += n;
      any = true;
    }
  }
  return any ? String(total) : '';
}

/**
 * 법인 주주 Share Application 부수 id 오프셋 — shapp_{CORP_SHAPP_BASE + N}/ 는 cs_{N}/ 법인 주주.
 *   개인 주주 부수(shapp_{CAF sh 행 id})와 id 가 겹치지 않게 한다.
 */
export const CORP_SHAPP_BASE = 1000;

/** Corporate Shareholder Profile(cs_{N}/) 중 회사명이 있는 N — 실제 법인 주주 */
function corporateShareholderIds(values: Record<string, string>): number[] {
  const out: number[] = [];
  for (const k of Object.keys(values)) {
    const m = /^cs_(\d+)\/cs_company_name$/.exec(k);
    if (m && str(values[k])) out.push(Number(m[1]));
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

/**
 * 같은 사람의 주소 — 이사(dir)·주주(sh)·개인 컨트롤러(ictrl) 행에서 ID 번호(우선) 또는 이름이 같은 행의 주소.
 *   UBO 행에 주소가 없을 때 Principal 주소 보완용.
 */
export function personAddressFallback(values: Record<string, string>, name: string, idNo: string): string {
  const norm = (s: unknown) => str(s).replace(/\s+/g, ' ').toUpperCase();
  const nm = norm(name);
  const id = norm(idNo).replace(/\s+/g, '');
  if (!nm && !id) return '';
  const sources: Array<[RegExp, string, string, string]> = [
    [/^dir(\d+)_director_name$/, 'dir', 'director_identification_no', 'director_address'],
    [/^sh(\d+)_shareholder_name$/, 'sh', 'shareholder_identification_no', 'shareholder_address'],
    [/^ictrl(\d+)_controller_full_name$/, 'ictrl', 'controller_identification_no', 'controller_residential_address'],
  ];
  let byName = '';
  for (const [re, prefix, idKey, addrKey] of sources) {
    for (const k of Object.keys(values)) {
      const m = re.exec(k);
      if (!m) continue;
      const addr = str(values[`${prefix}${m[1]}_${addrKey}`]);
      if (!addr) continue;
      const rowId = norm(values[`${prefix}${m[1]}_${idKey}`]).replace(/\s+/g, '');
      if (id && rowId && rowId === id) return addr;
      if (!byName && nm && norm(values[k]) === nm) byName = addr;
    }
  }
  return byName;
}

/** 개인 신분증 번호 → Share Application "Identification Type" 표기 */
function individualIdTypeLabel(idNo: string): string {
  const id = str(idNo).replace(/\s+/g, '').toUpperCase();
  if (!id) return '';
  if (/^[ST]\d{7}[A-Z]$/.test(id)) return 'NRIC';
  if (/^[FGM]\d{7}[A-Z]$/.test(id)) return 'FIN';
  return 'Passport';
}

export interface AuraDocCounts {
  /** Share Application 부수 id — CAF 개인 주주 행 id + (CORP_SHAPP_BASE + 법인 주주 cs N) */
  shareAppIds: number[];
  /** CAF 이사 행 id = DDF 부수(ddf_{id}/) */
  ddfIds: number[];
}

/** 디렉터·주주 중 외국 국적자 (XX Details of Foreign Director or Shareholder 용) */
export function collectForeignPersons(
  values: Record<string, string>,
): { name: string; idNo: string }[] {
  const out: { name: string; idNo: string }[] = [];
  const seen = new Set<string>();
  const push = (name: string, idNo: string) => {
    const nm = str(name);
    if (!nm || seen.has(nm.toLowerCase())) return;
    seen.add(nm.toLowerCase());
    out.push({ name: nm, idNo: str(idNo) });
  };

  const dirMax = maxIndexed(values, 'dir', 'director_name');
  for (let i = 1; i <= dirMax; i++) {
    if (!isForeignNationality(values[`dir${i}_director_nationality`])) continue;
    push(values[`dir${i}_director_name`], values[`dir${i}_director_identification_no`]);
  }
  const shMax = maxIndexed(values, 'sh', 'shareholder_name');
  for (let i = 1; i <= shMax; i++) {
    if (!isForeignNationality(values[`sh${i}_shareholder_nationality`])) continue;
    push(values[`sh${i}_shareholder_name`], values[`sh${i}_shareholder_identification_no`]);
  }
  return out;
}

/**
 * Aura 문서 값 채우기.
 * @returns 문서를 몇 부 만들어야 하는지(주주 수 / 이사 수) — 패키지가 슬롯을 늘리는 데 사용.
 */
export function syncAuraDocValues(
  values: Record<string, string>,
  setValue: SetValue,
): AuraDocCounts {
  const v = (k: string) => str(values[k]);
  const put = (target: string, value: string) => fillIf(values, setValue, target, value);
  const follow = (target: string, value: string) => followSource(values, setValue, target, value);

  // ── 회사 공통 ──
  const company = v('company_name') || v('entity_name') || v('proposed_company_name');
  const regNo = v('company_registration_no') || v('entity_registration_no') || v('entity_uen');
  const regOffice =
    v('registered_office_address') || v('entity_registered_address') || v('registered_address');
  const incDate = v('date_of_incorporation') || v('entity_incorporation_date');
  const fye = v('financial_year_end') || v('financial_year_end_date') || v('entity_proposed_fye');

  put('registered_office_address', regOffice);
  put('date_of_incorporation', incDate);
  put('financial_year_end', fye);

  // CAF 에 실제로 남아 있는 행만 (× 로 지운 행은 값이 비워져 빠진다)
  const dirIds = namedIds(values, 'dir', 'director_name');
  const shIds = namedIds(values, 'sh', 'shareholder_name');

  // ── First BOD (회사 1부) ──
  //    Number_Of_Shares = 회사 총 발행주식수(주주 배정분 합계), Price_Per_Share = 주당 가격
  const totalShares = sumNumeric(shIds.map((i) => v(`sh${i}_shareholder_number_of_shares`)));
  put('number_of_shares', totalShares || v('entity_share_capital'));
  put('price_per_share', v('entity_share_value'));

  //    First Directors 문구·서명표는 이사 **전원**이다 — 템플릿이 [[first_director_names]] 와
  //    [[if directors]] 반복행을 쓰고, normalize 가 dir{N}_* 에서 둘 다 만든다(여기서 채울 것 없음).
  //    회사 대표 서명 이사 = CAF 의 첫 번째 이사 행 (1번 행을 지웠으면 다음 행)
  const firstDir = dirIds[0];
  const d1Name = firstDir ? v(`dir${firstDir}_director_name`) : '';
  const secId = namedIds(values, 'sec', 'secretary_name')[0];
  follow('secretary_name', secId ? v(`sec${secId}_secretary_name`) : '');

  // ── Incorporation Documents (회사 1부) ──
  //    회사측 서명란(Name: …) = 대표 이사, 노미니 계약의 Principal = UBO
  //    ⑦ The Company 블록(company_director_name)은 normalize 가 director_name 을 그대로 쓴다.
  follow('director_name', d1Name);
  follow('authorised_person_name', d1Name);
  //    Declaration for Registers — 원본은 세 등록부 모두 'Registered office of the company' 체크
  put('roc_at_company', 'yes');
  put('rond_at_company', 'yes');
  put('rons_at_company', 'yes');
  follow('director_identification_no', firstDir ? v(`dir${firstDir}_director_identification_no`) : '');
  follow('director_signature', d1Name);
  follow('signing_director_signature', d1Name);
  follow('declarant_signature', d1Name);
  follow('company_director_signature', d1Name);
  follow('client_name', d1Name);
  if (d1Name) put('client_designation', 'Director');
  follow('client_signature', d1Name);

  const uboId = namedIds(values, 'ubo', 'ubo_name')[0];
  const uboName = uboId ? v(`ubo${uboId}_ubo_name`) : '';
  //   Principal(UBO) 주소 — UBO 행 주소가 비면 같은 사람(ID·이름)의 이사/주주/컨트롤러 주소로 (고객 회신 2026-10-26:
  //   법인 주주의 UBO 행엔 주소를 안 넣고 이사 행에만 넣는 경우 노미니 계약의 Principal 주소가 비었다).
  const uboAddr = uboId
    ? v(`ubo${uboId}_ubo_residential_address`) ||
      personAddressFallback(values, v(`ubo${uboId}_ubo_name`), v(`ubo${uboId}_ubo_identification_no`))
    : '';
  //    Annexure B "Office Location: [[office_loc]]" — 등록주소 서비스 위치 = 회사 등록주소
  //    (Aura 가 등록주소를 제공하면 Aura 사무실 주소). 등록주소를 바꾸면 따라가고, 직접 고치면 유지.
  follow('office_loc', regOffice);
  follow('ubo_name', uboName);
  follow('ubo_identification_no', uboId ? v(`ubo${uboId}_ubo_identification_no`) : '');
  follow('ubo_residential_address', uboAddr);
  follow('principal_signature', uboName);
  // 증인(witness) 은 T-Link 원천이 없어 비워 둔다.

  // ── Share Application Form (주주 1인당 1부, scope shapp_{id}/) ──
  //   화면·템플릿(신버전)은 shareholder_* 키를 쓴다 — 예전엔 applicant_* 만 채워 화면이 비었다.
  //   개인 주주 = CAF sh{N} 행(id N), 법인 주주 = Corporate Shareholder Profile cs_{N}/ (id CORP_SHAPP_BASE+N).
  const fillShareApp = (sc: string, p: {
    name: string; idNo: string; idType: string; nationality: string;
    residential: string; contact: string; email: string; mobile: string;
    shares: string; shareType: string; payable: string; currency: string;
  }) => {
    put(sc + 'company_name', company);
    put(sc + 'registered_office_address', regOffice);
    follow(sc + 'shareholder_name', p.name);
    follow(sc + 'shareholder_identification_no', p.idNo);
    follow(sc + 'shareholder_identification_type', p.idType);
    follow(sc + 'shareholder_nationality', p.nationality);
    follow(sc + 'shareholder_residential_address', p.residential);
    follow(sc + 'shareholder_contact_address', p.contact);
    follow(sc + 'shareholder_email_address', p.email);
    follow(sc + 'shareholder_contact_number', p.mobile);
    follow(sc + 'number_of_shares', p.shares);
    follow(sc + 'type_of_shares', p.shareType);
    follow(sc + 'total_amount_payable', p.payable);
    follow(sc + 'currency', p.currency);
    follow(sc + 'shareholder_signature', p.name);
  };
  for (const i of shIds) {
    const idNo = v(`sh${i}_shareholder_identification_no`);
    const addr = v(`sh${i}_shareholder_address`);
    fillShareApp(`shapp_${i}/`, {
      name: v(`sh${i}_shareholder_name`),
      idNo,
      idType: individualIdTypeLabel(idNo),
      nationality: v(`sh${i}_shareholder_nationality`),
      residential: addr,
      contact: addr,
      email: v(`sh${i}_shareholder_email`),
      mobile: v(`sh${i}_shareholder_mobile_no`),
      shares: v(`sh${i}_shareholder_number_of_shares`),
      shareType: v(`sh${i}_shareholder_share_type`),
      payable: v(`sh${i}_shareholder_paid_up_capital`),
      currency: v(`sh${i}_shareholder_currency`),
    });
  }
  const corpShIds = corporateShareholderIds(values);
  for (const n of corpShIds) {
    const cs = `cs_${n}/`;
    const addr = v(cs + 'cs_registered_address');
    fillShareApp(`shapp_${CORP_SHAPP_BASE + n}/`, {
      name: v(cs + 'cs_company_name'),
      idNo: v(cs + 'cs_registration_no'),
      idType: v(cs + 'cs_registration_no') ? 'Company Registration No.' : '',
      nationality: v(cs + 'cs_country_of_incorporation'),
      residential: addr,
      contact: addr,
      email: v(cs + 'cs_email'),
      mobile: v(cs + 'cs_mobile_no') || v(cs + 'cs_contact_no_office'),
      shares: v(cs + 'cs_allot_number_of_shares'),
      shareType: v(cs + 'cs_allot_share_type'),
      payable: v(cs + 'cs_allot_paid_up_capital'),
      currency: v(cs + 'cs_allot_currency'),
    });
  }
  const shareAppIds = [...shIds, ...corpShIds.map((n) => CORP_SHAPP_BASE + n)];

  // ── DDF (이사 1인당 1부, scope ddf_{id}/ — id 는 CAF 이사 행 id 와 같다) ──
  //    Registered Holder = 본인 명의 보유분 기준이므로 해당 이사 본인.
  //    이해관계 내역(설명·금액·취득일) 은 원천이 없어 비워 둔다.
  for (const i of dirIds) {
    const sc = `ddf_${i}/`;
    const name = v(`dir${i}_director_name`);
    put(sc + 'company_name', company);
    follow(sc + 'director_name', name);
    follow(sc + 'registered_holder', name);
    // ⚠ 이해관계 구분 조건 기본값.
    //   DDF 원본은 값들이 [[if own_name_interests]] … 같은 조건 블록 안에 있어서,
    //   이 플래그가 payload 에 없으면 조건이 거짓 → **표 행이 통째로 삭제**되고
    //   입력한 Registered Holder·금액도 함께 사라진다.
    //   화면 토글(AuraDdfDoc)이 값을 덮어쓰므로 여기서는 '없을 때만' 기본값을 심는다.
    put(sc + 'other_interests', 'yes');
    put(sc + 'own_name_interests', 'yes');
    put(sc + 'family_interests', 'no');
    put(sc + 'deemed_interests', 'no');
    put(sc + 'corporate_interests', 'no');
  }

  // DDF 조건 플래그는 **생성되는 모든 부수**에 있어야 한다.
  //   이사 정보가 아직 없어도 DDF 는 1부 생성되므로, 이미 존재하는
  //   ddf_{N}/ 스코프와 최소 1부(ddf_1/)에 대해 기본값을 보장한다.
  const ddfScopes = new Set<string>(['ddf_1/']);
  for (const i of dirIds) ddfScopes.add(`ddf_${i}/`);
  for (const k of Object.keys(values)) {
    const m = /^(ddf_\d+\/)/.exec(k);
    if (m) ddfScopes.add(m[1]);
  }
  for (const sc of ddfScopes) {
    put(sc + 'company_name', company);
    put(sc + 'other_interests', 'yes');
    put(sc + 'own_name_interests', 'yes');
    put(sc + 'family_interests', 'no');
    put(sc + 'deemed_interests', 'no');
    put(sc + 'corporate_interests', 'no');
  }

  // ── Form 45A (법원 허가 특수 케이스) — 회사 정보만. 인물은 Import Director 로 ──
  const f45aScopes = new Set<string>();
  for (const k of Object.keys(values)) {
    const m = /^(f45a_\d+\/)/.exec(k);
    if (m) f45aScopes.add(m[1]);
  }
  for (const sc of f45aScopes) {
    put(sc + 'company_name', company);
    put(sc + 'company_registration_no', regNo);
  }

  // ── 외국인 디렉터/주주 표 (회사 1부, 행 반복) ──
  collectForeignPersons(values).forEach((p, i) => {
    const id = i + 1;
    follow(`fp${id}_foreign_person_name`, p.name);
    follow(`fp${id}_foreign_person_passport_fin_no`, p.idNo);
    // 여권 만료일 / 조치사항은 T-Link 원천이 없어 비워 둔다.
  });

  return { shareAppIds, ddfIds: dirIds };
}
